
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  fetchGiftTrackingRecords,
  fetchContactGiftRecords,
  fetchOrganizationGiftRecords,
  fetchGiftRecordById,
  createGiftRecord,
  updateGiftRecord,
  deleteGiftRecord,
  getGiftAnalytics
} from "@/services";

export function useGiftTracking() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // All gift tracking records
  const giftRecordsQuery = useQuery({
    queryKey: ['giftRecords'],
    queryFn: () => fetchGiftTrackingRecords()
  });

  // Contact's gift records
  const getContactGiftRecordsQuery = (contactId: string) => useQuery({
    queryKey: ['giftRecords', 'contact', contactId],
    queryFn: () => fetchContactGiftRecords(contactId),
    enabled: !!contactId
  });

  // Organization's gift records
  const getOrganizationGiftRecordsQuery = (organizationId: string) => useQuery({
    queryKey: ['giftRecords', 'organization', organizationId],
    queryFn: () => fetchOrganizationGiftRecords(organizationId),
    enabled: !!organizationId
  });

  // Single gift record
  const getGiftRecordQuery = (id: string) => useQuery({
    queryKey: ['giftRecords', id],
    queryFn: () => fetchGiftRecordById(id),
    enabled: !!id
  });

  // Create gift record
  const createGiftRecordMutation = useMutation({
    mutationFn: createGiftRecord,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['giftRecords'] });
      if (data.contact_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['giftRecords', 'contact', data.contact_id] 
        });
      }
      if (data.organization_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['giftRecords', 'organization', data.organization_id] 
        });
      }
      toast({
        title: "Gift record created",
        description: "The gift record has been created successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to create gift record",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Update gift record
  const updateGiftRecordMutation = useMutation({
    mutationFn: ({id, data}: {id: string; data: any}) => 
      updateGiftRecord(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['giftRecords'] });
      queryClient.invalidateQueries({ queryKey: ['giftRecords', data.id] });
      if (data.contact_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['giftRecords', 'contact', data.contact_id] 
        });
      }
      if (data.organization_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['giftRecords', 'organization', data.organization_id] 
        });
      }
      toast({
        title: "Gift record updated",
        description: "The gift record has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to update gift record",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Delete gift record
  const deleteGiftRecordMutation = useMutation({
    mutationFn: deleteGiftRecord,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['giftRecords'] });
      toast({
        title: "Gift record deleted",
        description: "The gift record has been deleted successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to delete gift record",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Gift analytics
  const getGiftAnalyticsQuery = (year?: number) => useQuery({
    queryKey: ['giftAnalytics', year],
    queryFn: () => getGiftAnalytics(year),
  });

  return {
    giftRecords: giftRecordsQuery.data || [],
    isLoading: giftRecordsQuery.isLoading,
    error: giftRecordsQuery.error,
    
    getContactGiftRecords: getContactGiftRecordsQuery,
    getOrganizationGiftRecords: getOrganizationGiftRecordsQuery,
    getGiftRecord: getGiftRecordQuery,
    getGiftAnalytics: getGiftAnalyticsQuery,
    
    createGiftRecord: createGiftRecordMutation.mutate,
    updateGiftRecord: updateGiftRecordMutation.mutate,
    deleteGiftRecord: deleteGiftRecordMutation.mutate
  };
}
